import React from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth, DEN_URL, PAWS_URL } from '../App';
import { LayoutDashboard, CalendarDays, Scissors, Users, MapPin, LogOut, Sun, Moon, BarChart3, Tag, Briefcase, TrendingUp, MessageSquare, FileText } from 'lucide-react';
import { useTheme } from '../App';

const LOGO_COLOR = 'https://customer-assets.emergentagent.com/job_paw-portal-3/artifacts/ykqrxquz_Petfolk%20Logo%20Color%20TransparentBG.png';
const PAW_WHITE = 'https://customer-assets.emergentagent.com/job_paw-portal-3/artifacts/ykrw1xjt_Petfolk%20Paw%20White.png';

const NAV = [
  { to: '/den', label: 'Dashboard', icon: LayoutDashboard, exact: true },
  { to: '/den/bookings', label: 'Bookings', icon: CalendarDays },
  { to: '/den/services', label: 'Services', icon: Scissors },
  { to: '/den/coupons', label: 'Coupons', icon: Tag },
  { to: '/den/users', label: 'Users', icon: Users },
  { to: '/den/nodes', label: 'Locations', icon: MapPin },
  { to: '/den/analytics', label: 'Analytics', icon: BarChart3 },
  { to: '/den/leads', label: 'Franchise Leads', icon: Briefcase },
];
const MASTER_NAV = [
  { to: '/den/financials', label: 'Financials', icon: TrendingUp },
  { to: '/den/templates', label: 'Templates', icon: MessageSquare },
  { to: '/den/blog', label: 'Blog', icon: FileText },
];
const EDITOR_NAV = [
  { to: '/den/blog', label: 'Blog', icon: FileText },
];

export default function DenLayout() {
  const { user, logout, setShowAuth } = useAuth();
  const { darkMode, toggleDark } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();

  if (!user || user.role === 'CUSTOMER') {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg-primary)' }}>
        <div className="text-center">
          <h2 className="font-outfit text-2xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>Admin Access Required</h2>
          <p className="mb-4 text-sm" style={{ color: 'var(--text-secondary)' }}>Sign in with an admin account to access the Den</p>
          <button onClick={() => setShowAuth(true)} className="btn-primary" data-testid="den-login-btn">Sign In</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex" style={{ background: 'var(--bg-primary)' }}>
      {/* Sidebar */}
      <aside className="hidden md:flex flex-col w-64 border-r shrink-0 sticky top-0 h-screen" style={{ borderColor: 'var(--border-color)', background: 'var(--bg-card)' }} data-testid="den-sidebar">
        <div className="p-6 border-b" style={{ borderColor: 'var(--border-color)' }}>
          <Link to="/den" className="flex items-center gap-3">
            <img src={LOGO_COLOR} alt="Petfolk" className="h-8 w-auto object-contain" />
            <div>
              <p className="text-xs font-semibold" style={{ color: 'var(--text-secondary)' }}>Den — {user.role.replace(/_/g, ' ')}</p>
            </div>
          </Link>
        </div>
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {NAV.map(n => {
            const active = n.exact ? location.pathname === n.to : location.pathname.startsWith(n.to);
            return (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`den-nav-${n.label.toLowerCase().replace(' ', '-')}`}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                  active ? 'bg-petfolk-orange/10 text-petfolk-orange font-semibold' : 'hover:bg-black/5 dark:hover:bg-white/5'
                }`}
                style={{ color: !active ? 'var(--text-secondary)' : undefined }}
              >
                <n.icon size={18} />
                {n.label}
              </Link>
            );
          })}
          {/* Groomer-specific quick link */}
          {['GROOMER', 'STORE_STAFF'].includes(user.role) && (
            <Link
              to="/den/groomer"
              data-testid="den-nav-my-sessions"
              className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                location.pathname === '/den/groomer' ? 'bg-petfolk-orange/10 text-petfolk-orange font-semibold' : 'hover:bg-black/5 dark:hover:bg-white/5'
              }`}
              style={{ color: location.pathname !== '/den/groomer' ? 'var(--text-secondary)' : undefined }}
            >
              ✂️ My Sessions
            </Link>
          )}
          {/* Editor-only quick link */}
          {user.role === 'EDITOR' && (
            <>
              {EDITOR_NAV.map(n => {
                const active = location.pathname.startsWith(n.to);
                return (
                  <Link key={n.to} to={n.to}
                    data-testid={`den-nav-${n.label.toLowerCase()}`}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${active ? 'bg-petfolk-orange/10 text-petfolk-orange font-semibold' : 'hover:bg-black/5 dark:hover:bg-white/5'}`}
                    style={{ color: !active ? 'var(--text-secondary)' : undefined }}
                  >
                    <n.icon size={18} />
                    {n.label}
                  </Link>
                );
              })}
            </>
          )}
          {/* Master Admin only links */}
          {user.role === 'MASTER_ADMIN' && (
            <>
              <div className="px-3 py-1 mt-2">
                <p className="text-xs font-bold uppercase tracking-wider" style={{ color: 'var(--text-secondary)', opacity: 0.5 }}>Master Admin</p>
              </div>
              {MASTER_NAV.map(n => {
                const active = location.pathname.startsWith(n.to);
                return (
                  <Link key={n.to} to={n.to}
                    data-testid={`den-nav-${n.label.toLowerCase()}`}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${active ? 'bg-petfolk-orange/10 text-petfolk-orange font-semibold' : 'hover:bg-black/5 dark:hover:bg-white/5'}`}
                    style={{ color: !active ? 'var(--text-secondary)' : undefined }}
                  >
                    <n.icon size={18} />
                    {n.label}
                  </Link>
                );
              })}
            </>
          )}
        </nav>
        <div className="p-4 border-t" style={{ borderColor: 'var(--border-color)' }}>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-petfolk-orange to-petfolk-purple flex items-center justify-center text-white text-xs font-bold">
                {user.name?.[0]}
              </div>
              <div>
                <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{user.name}</p>
                <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>{user.email}</p>
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={toggleDark} className="flex-1 p-2 rounded-xl text-xs hover:bg-black/5 dark:hover:bg-white/5 flex items-center justify-center gap-1" style={{ color: 'var(--text-secondary)' }}>
              {darkMode ? <Sun size={14} /> : <Moon size={14} />} Theme
            </button>
            <button onClick={() => { logout(); navigate('/den'); }} className="flex-1 p-2 rounded-xl text-xs text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center justify-center gap-1">
              <LogOut size={14} /> Logout
            </button>
          </div>
          <a href={PAWS_URL} className="block text-center text-xs mt-2 py-1 text-petfolk-orange hover:underline">← Go to Customer Site</a>
        </div>
      </aside>

      {/* Mobile Top Bar */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-50 glass border-b p-3 flex items-center justify-between" style={{ borderColor: 'var(--border-color)' }}>
        <Link to="/den" className="flex items-center gap-2">
          <img src={PAW_WHITE} alt="Petfolk" className="h-7 w-auto" />
          <span className="font-outfit font-bold gradient-text">Den</span>
        </Link>
      </div>

      {/* Mobile Bottom Nav */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-40 glass border-t flex justify-around py-2" style={{ borderColor: 'var(--border-color)' }}>
        {NAV.map(n => {
          const active = n.exact ? location.pathname === n.to : location.pathname.startsWith(n.to);
          return (
            <Link key={n.to} to={n.to} className={`flex flex-col items-center gap-0.5 px-3 py-1 text-xs ${active ? 'text-petfolk-orange' : ''}`} style={{ color: !active ? 'var(--text-secondary)' : undefined }}>
              <n.icon size={20} />
              {n.label}
            </Link>
          );
        })}
      </div>

      {/* Content */}
      <main className="flex-1 p-4 sm:p-8 pb-20 md:pb-8 overflow-auto md:pt-8 pt-16">
        <Outlet />
      </main>
    </div>
  );
}
